<template>
  <BaseSelectDropdown
    ref="dropdown"
    v-model="dropdownActive"
    :label="label"
    :large="large"
    :allow-width-overflow="true"
    :dropdown-width="534"
    :popper-class="popperClass"
    class="tree-select"
    select-dropdown-class="tree-select-dropdown"
    @close="handleCancel"
    @handle-click-navigation="handleClickNavigation"
  >
    <template slot="header">
      <div
        v-overflow-title="valueText"
        class="tree-select__content"
        :class="{'tree-select__content_empty': !checkedCount}"
      >
        <template v-if="checkedCount">
          {{ valueText }}
        </template>
        <template v-else>
          <span class="tree-select__placeholder">
            {{ placeholder }}
          </span>
        </template>
      </div>
    </template>
    <template slot="dropdown">
      <TreeSelectDropdown
        :readonly="readonly"
        :tree-is-loading="treeIsLoading"
        :flat-choices="flatChoices"
        :selected-items="activeNodes"
        :disable-ok-button="disableOkButton"
        :nodes-parent-map="nodesParentMap"
        :selected-items-text="selectedItemsText"
        :search-text="searchText"
        :leaf-nodes="leafNodes"
        :no-child-text-getter="noChildTextGetter"
        :is-leaf-node-predicate="isLeafNodePredicate"
        @addNode="handleAddNode"
        @searchTextInput="handleSearchTextChange"
        @removeNode="handleRemoveNode"
        @resetAll="handleResetAll"
        @submit="handleSubmit"
        @cancel="handleCancel"
        @handle-click-navigation="handleClickNavigation"
      />
    </template>
  </BaseSelectDropdown>
</template>

<script>
  import BaseSelectDropdown from 'components/Inputs/Select/common/BaseSelectDropdown/BaseSelectDropdown.vue';
  import TreeSelectDropdown from 'components/Inputs/Select/TreeSelect/TreeSelectDropdown.vue';
  import getTr from 'translation';
  import _ from 'lodash';

  export default {
    name: 'TreeSelect',
    components: {TreeSelectDropdown, BaseSelectDropdown},
    props: {
      label: {
        type: String,
        default: ''
      },
      large: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        default: ''
      },
      flatChoices: {
        type: Object,
        default: Object
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      value: {
        type: Array,
        default: Array
      },
      selectedItemsText: {
        type: String,
        default: () => getTr('items_chosen_format')
      },
      leafNodesFiltrationPredicate: {
        type: Function,
        default: () => true
      },
      treeIsLoading: {
        type: Boolean,
        default: false
      },
      popperClass: {
        type: String,
        default: ''
      },
      disableOkButton: {
        type: Object,
        default: Object
      },
      readonly: {
        type: Boolean,
        default: false
      },
      noChildTextGetter: {
        type: Function,
        default: () => 'No items found'
      },
      isLeafNodePredicate: {
        type: Function,
        default: undefined
      }
    },
    data () {
      return {
        dropdownActive: false,

        searchText: '',
        innerValue: []
      };
    },
    computed: {
      nodesParentMap () {
        const choices = Object.keys(this.flatChoices).map(i => this.flatChoices[i]).
          filter(c => c.parentId);

        return _.groupBy(choices, 'parentId');
      },
      leafNodes () {
        return this.activeNodes.map(i => this.flatChoices[i]).filter(
          i => {
            if (!i) {
              return false;
            }
            return !this.nodesParentMap[i.id];
          }
        ).
          filter(this.leafNodesFiltrationPredicate);
      },
      activeNodes (){
        const activeIds = [];

        const enableChildNodes = (nodeId) => {
          const firstChildNodesList = this.nodesParentMap[nodeId];
          if (!firstChildNodesList) {
            return;
          }
          firstChildNodesList.forEach(childNode => {
            activeIds.push(childNode.id);
            enableChildNodes(childNode.id);
          });
        };

        this.innerValue.forEach(item => {
          activeIds.push(item);
          enableChildNodes(item);
        });

        this.$emit('selected-items', activeIds);
        return activeIds;
      },
      checkedCount () {
        return this.leafNodes.length;
      },
      valueText () {
        if (this.checkedCount !== 1) {
          return this.selectedItemsText.format(this.checkedCount);
        }
        const item = this.leafNodes[0];
        return this.itemText(item);
      }
    },
    watch: {
      dropdownActive () {
        if (!this.dropdownActive) {
          this.onFocusCurrentComponent();
        }
      },
      value: {
        immediate: true,
        handler () {
          this.innerValue = this.value;
        }
      }
    },
    methods: {
      onFocusCurrentComponent () {
        this.$refs.dropdown?.$el.focus();
      },
      clickFocusElement () {
        const targetElem = document.activeElement.querySelector('[data-checkbox]');
        if (targetElem) {
          targetElem.click();
        }
      },
      controlToggleDropdownNode () {
        const targetElem = document.activeElement.querySelector('[data-dropdown]');
        if (targetElem) {
          targetElem.click();
        }
      },
      controlCheckbox (controlType) {
        let nextIndex;

        const focusableElements = Array.from(document.querySelectorAll('.is-visible.focus-element'));
        const currentIndex = focusableElements.indexOf(document.activeElement);

        if (controlType === 'next') {
          if (currentIndex + 1 === focusableElements.length) {
            return;
          }

          nextIndex = (currentIndex + 1) % focusableElements.length;
        } else if (controlType === 'prev') {
          if (currentIndex === 0) {
            return;
          }

          nextIndex = (currentIndex - 1) % focusableElements.length;
        }

        focusableElements[nextIndex].focus();
      },
      handleClickNavigation (e) {
        e.stopPropagation();

        if (['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft', 'Enter'].includes(e.key) && this.dropdownActive === false) {
          this.dropdownActive = true;
          return;
        }

        switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          this.controlToggleDropdownNode();
          break;
        case 'ArrowUp':
          this.controlCheckbox('prev');
          break;
        case 'ArrowDown':
          this.controlCheckbox('next');
          break;
        case 'Enter':
          this.clickFocusElement();

          break;
        case 'Escape':
          this.handleSubmit();
          break;
        default:
          break;
        }
      },
      handleSearchTextChange (newVal) {
        this.searchText = newVal;
        this.$emit('searchTextChange', newVal);
      },
      handleSubmit () {
        this.$emit('input', this.innerValue);
        this.dropdownActive = false;
      },
      handleCancel () {
        this.innerValue = this.value;
        this.dropdownActive = false;
        this.searchText = '';
      },
      itemText (item) {
        return item.name;
      },
      getChoiceById (id) {
        return this.flatChoices[id];
      },
      handleResetAll () {
        this.innerValue = [];
      },
      uncheckNode (nodeId) {
        // uncheck if it is selected
        let activeNodesSet = new Set(this.innerValue.filter(i => i !== nodeId));
        const inactiveNodesList = new Set();

        // uncheck strict sequence of inactiveNodesList. Don't touch their siblings
        const unckeckNodeHierarchy = (parentId) => {
          const childNodes = this.nodesParentMap[parentId] ? this.nodesParentMap[parentId] : [];
          childNodes.map(i => i.id).forEach(childId => {
            if (inactiveNodesList.has(childId)) {
              inactiveNodesList.delete(childId);

              if (inactiveNodesList.size) {
                unckeckNodeHierarchy(childId);
              }
            } else {
              activeNodesSet.add(childId);
            }
          });
        };

        const backPropagateChildNodes = (parentId, disabledChildId) => {
          const childNodeIds = this.nodesParentMap[parentId].map(i => i.id).filter(i => i !== disabledChildId);
          childNodeIds.forEach(i => activeNodesSet.add(i));
          if (inactiveNodesList.size) {
            unckeckNodeHierarchy(disabledChildId);
          }
        };

        const iterParentNode = (nodeId) => {
          const node = this.flatChoices[nodeId];
          const parentId = node.parentId;
          if (!parentId) {
            inactiveNodesList.clear();
            return;
          }
          if (activeNodesSet.has(parentId)) {
            activeNodesSet.delete(parentId);
            backPropagateChildNodes(parentId, nodeId);
            return;
          }
          inactiveNodesList.add(nodeId);
          iterParentNode(parentId);
        };

        iterParentNode(nodeId);

        this.innerValue = Array.from(activeNodesSet);
      },
      checkNode (nodeId) {
        let activeNodesSet = new Set([...this.innerValue, nodeId]);

        const iterChildNodes = nodeId => {
          const childNodes = this.nodesParentMap[nodeId] || [];
          childNodes.forEach(childNode => {
            const childNodeId = childNode.id;
            if (activeNodesSet.has(childNodeId)) {
              activeNodesSet.delete(childNodeId);
            }
            iterChildNodes(childNodeId);
          });
        };

        iterChildNodes(nodeId);

        const iterParent = nodeId => {
          const node = this.flatChoices[nodeId];
          const parentId = node.parentId;
          if (!parentId) {
            return;
          }

          const parentNode = this.flatChoices[parentId];

          // do not check parent node if it is not selectable
          if (parentNode.selectable !== undefined && !parentNode.selectable) {
            return;
          }

          const childNodeIds = this.nodesParentMap[parentId].map(i => i.id);
          const childCount = childNodeIds.length;
          const activeChildNodes = childNodeIds.filter(i => activeNodesSet.has(i));
          if (activeChildNodes.length === childCount) {
            childNodeIds.forEach(i => activeNodesSet.delete(i));
            activeNodesSet.add(node.parentId);
            return iterParent(node.parentId);
          }
        };

        iterParent(nodeId);

        this.innerValue = Array.from(activeNodesSet);
      },
      handleAddNode (nodeId) {
        this.checkNode(nodeId);
      },
      handleRemoveNode (nodeId) {
        this.uncheckNode(nodeId);
      }
    }
  };
</script>

<style>
  .tree-select-dropdown.select-items {
    height: 387px;
    max-height: unset;

    border: none;
  }

  .tree-select__placeholder {
    color: var(--gray-text-color);
  }
</style>
